import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useClientPersonalInformationContext } from "../../api/clientPersonalInformation";
import ApiError from "../../api/common/apiError";
import { useOrganizationContext } from "../../api/current-organization/organizationContext";
import { fetchLocationOptions, fetchLocalities } from "../../api/locations";
import useAccessToken from "../../auth/useAccessToken";
import Button from "../../components/Button";
import { FormDropdownOption } from "../../components/FormDropdown";
import FormImageUploadInput, { useFormFileUploadInputState } from "../../components/FormFileUploadInput";
import FormTextAreaInput from "../../components/FormTextAreaInput";
import FormTextInput from "../../components/FormTextInput";
import GeneralFormError from "../../components/GeneralFormError";
import { successToast } from "../../toast";
import { clientProfileEditTooltips } from "../../tooltipsContent";
import { ClientPersonalDetailsFormValues, useClientPersonalDetailsForm } from "./ClientPersonalDetailsFormValues";
import { useAppPaths } from "../../Routes";
import FormAutocomplete from "../../components/FormAutocomplete";
import useSearchLocalities from "../../utils/useSearchLocalities";

const ClientPersonalDetailsForm = () => {
    const [allLocationOptions, setAllLocationOptions] = useState<FormDropdownOption<string>[]>([]);
    const [isLoadingLocationOptions, setIsLoadingLocationOptions] = useState(true);
    const [isClientPersonalInformationLoaded, setIsClientPersonalInformationLoaded] = useState(false);
    const [submissionError, setSubmissionError] = useState<ApiError | undefined>(undefined);
    const [selectedLocationLocalities, setSelectedLocationLocalities] = useState<FormDropdownOption<string>[]>([]);

    const navigate = useNavigate();
    const appPaths = useAppPaths();

    const accessToken = useAccessToken();
    const { giggedClientTerminology, organizationConfig } = useOrganizationContext();
    const {
        clientPersonalInformation,
        updateClientPersonalInformation,
        isUpdatingClientPersonalInformation,
        isLoading,
        isValidating,
        reload: reloadPersonalInformation,
        setClientProfileCompletionStatus
    } = useClientPersonalInformationContext();

    const clientFormInitialValues = {
        ...clientPersonalInformation,
        locationId: clientPersonalInformation?.location?.countryId || null,
        localityId: clientPersonalInformation?.location?.locality.localityId || null,
        location: undefined,
    };

    const methods = useClientPersonalDetailsForm(
        giggedClientTerminology,
        organizationConfig.hideGiggedClientCompanyInfo,
        clientFormInitialValues
    );
    const { reset, register, formState } = methods;
    const selectedLocationId = methods.watch("locationId") || "";
    const {
        urlOrDataUri: profileImageDataUri,
        loadFile,
        error: profileImageError,
        isDirty: isProfileImageDirty,
        reset: resetImageInput,
        ...fileInputProps
    } = useFormFileUploadInputState({
        maxFileSizeKb: 1000,
        initialUrl: clientPersonalInformation?.profileImageUrl,
    });

    useEffect(() => {
        if (isClientPersonalInformationLoaded) return;

        reset({
            ...clientPersonalInformation,
            locationId: clientPersonalInformation?.location?.countryId || "",
            aboutMe: clientPersonalInformation?.aboutMe || "",
            companyTagline: clientPersonalInformation?.companyTagline || "",
            companyWebsite: clientPersonalInformation?.companyWebsite || "",
            localityId: clientPersonalInformation?.location?.locality.localityId ? clientPersonalInformation.location.locality.localityId : null
        });

        if (clientPersonalInformation) {
            setIsClientPersonalInformationLoaded(true);
        }
    }, [reset, clientPersonalInformation, isClientPersonalInformationLoaded]);

    useEffect(() => {
        if (!clientPersonalInformation) return;
    
        // Reset the form with gig data using the values from gig
        methods.reset({
            ...clientPersonalInformation,
            locationId: clientPersonalInformation.location?.countryId || "",
            localityId: clientPersonalInformation.location?.locality.localityId || ""
        });
    
        // Pre-populate the localities dropdown with data from gig
        // if (clientPersonalInformation.localityId) {
        //     const localities = [
        //         {
        //             value: clientFormInitialValues.localityId,
        //             label: clientFormInitialValues.localityId,
        //         },
        //     ];
        //     setSelectedLocationLocalities(localities);
        // }
        if (clientPersonalInformation.location?.locality) {
            const localities = [
                {
                    value: clientPersonalInformation.location.locality.localityId,
                    label: `${clientPersonalInformation.location.locality.locality}${
                        clientPersonalInformation.location.locality.region ? ` (${clientPersonalInformation.location.locality.region})` : ""
                    }`,
                },
            ];
            setSelectedLocationLocalities(localities);
        }
    }, [clientPersonalInformation, methods.reset]);

    useEffect(() => {
        const locationId = methods.watch("locationId");
    
        if (locationId && locationId !== clientPersonalInformation?.location?.countryId) {
            console.log("Fetching localities for updated location:", locationId);
            fetchAndSetLocalities(locationId);
        }
    }, [methods.watch("locationId"), accessToken, clientPersonalInformation]);

    const fetchAndSetLocalities = (locationId: string) => {
        fetchLocalities(locationId, accessToken, "", 0, 2500)
            .then((response) => {
                const localities = response.items.map((locality) => ({
                    value: locality.localityId,
                    label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`,
                }));
                setSelectedLocationLocalities(localities);
                methods.setValue("localityId", "");
            })
            .catch((error) => {
                console.error("Error fetching localities:", error);
            });
    };

    useEffect(() => {
        const selectedLocationId = methods.watch("locationId");
        if (!selectedLocationId) {
            methods.setValue("localityId", ""); // Clear locality when no location selected
        }
    }, [methods.watch("locationId")]);

    useEffect(() => {
        if (accessToken) {
            const loadAllLocations = async () => {
                setIsLoadingLocationOptions(true);
                try {
                    const fetchedLocationOptions = await fetchLocationOptions("", accessToken);
                    setAllLocationOptions(fetchedLocationOptions as FormDropdownOption<string>[]);
                } finally {
                    setIsLoadingLocationOptions(false);
                }
            };
            loadAllLocations();
        }
    }, [accessToken]);

    const {
        localities,
        handleSearchChange,
    } = useSearchLocalities(fetchLocalities, selectedLocationId, accessToken);
    
    useEffect(() => {
        if (localities.length > 0) {
            const updatedLocalities = localities.map((locality) => ({
                value: locality.localityId,
                label: `${locality.locality}${locality.region ? ` (${locality.region})` : ""}`,
            }));
            setSelectedLocationLocalities(updatedLocalities);
        }
    }, [localities]);

    const handleSaveAndUpdate = async (values: ClientPersonalDetailsFormValues) => {
        const isProfileAlreadyComplete = clientPersonalInformation?.isProfileComplete;

        const response = await updateClientPersonalInformation({
            ...values,
            profileImageDataUri: isProfileImageDirty ? profileImageDataUri : undefined
        });

        if (response.success) {
            resetImageInput();
            successToast(`${giggedClientTerminology} details successfully updated.`);
            reloadPersonalInformation();
            methods.reset(values);  
            if (!isProfileAlreadyComplete) {
                setClientProfileCompletionStatus(true);
                navigate(appPaths.dashboard, { replace: true });
            }
            return;
        }

        setSubmissionError(response.error);
    };

    if (isLoading || isValidating) {
        return <p>Loading...</p>;
    }

    const showCompanyInfo = !organizationConfig.hideGiggedClientCompanyInfo;

    return (
        <form onSubmit={methods.handleSubmit(handleSaveAndUpdate)}>
            <div className="space-y-6">
                {showCompanyInfo && (
                    <div className="md:flex md:space-x-6 md:space-y-0">
                        <FormTextInput
                            required
                            label="Company name"
                            placeholder="Enter company name"
                            error={formState.errors.companyName}
                            tooltip={clientProfileEditTooltips.companyName}
                            {...register("companyName")}
                        />
                    </div>
                )}
                <div className="md:flex md:space-x-6 md:space-y-0">
                    <FormTextInput
                        required={!showCompanyInfo}
                        label={!showCompanyInfo ? `${giggedClientTerminology} name` : "Display name"}
                        placeholder={!showCompanyInfo ? `Enter ${giggedClientTerminology.toLowerCase()} name` : "Enter display name"}
                        error={formState.errors.name}
                        tooltip={showCompanyInfo ? clientProfileEditTooltips.displayNameOtm : clientProfileEditTooltips.displayNameItm(giggedClientTerminology)}
                        {...register("name")}
                    />
                </div>
                <FormTextAreaInput
                    required
                    id="client-personal-information-about-me"
                    label={organizationConfig.hideGiggedClientCompanyInfo ? `${organizationConfig.giggedClientTerminology} overview` : "Company overview"}
                    placeholder={`Write an overview about ${organizationConfig.hideGiggedClientCompanyInfo ? `this ${giggedClientTerminology.toLowerCase()}` : "your company"}`}
                    error={formState.errors.aboutMe}
                    tooltip={!organizationConfig.hideGiggedClientCompanyInfo ? clientProfileEditTooltips.companyOverview : clientProfileEditTooltips.clientOverview(giggedClientTerminology)}
                    register={register("aboutMe")}
                />
                {showCompanyInfo && (
                    <div className="space-y-6">
                        <div className="md:flex space-y-6 md:space-x-6 md:space-y-0">
                            {allLocationOptions.length > 0 && (
                                <div className="w-full md:w-1/2">
                                    <Controller
                                        name="locationId"
                                        control={methods.control}
                                        render={({ field: { onChange, value } }) => (
                                            <FormAutocomplete
                                                required
                                                label="Country"
                                                value={value || ""}
                                                options={allLocationOptions}
                                                disabled={isValidating || isLoading}
                                                onChange={onChange}
                                                error={methods.formState.errors.locationId}
                                                isLoadingOptions={isLoadingLocationOptions}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            <div className="w-full md:w-1/2">
                                {selectedLocationLocalities && selectedLocationLocalities.length > 0 && (
                                    <Controller
                                        name="localityId"
                                        control={methods.control}
                                        render={({ field: { onChange } }) => (
                                            <FormAutocomplete
                                                required
                                                label="City"
                                                value={methods.watch("localityId") || ""}
                                                options={selectedLocationLocalities}
                                                disabled={selectedLocationLocalities.length < 1 || isValidating || isLoading}
                                                onChange={onChange}
                                                onInputChange={(event) => {
                                                    handleSearchChange(event || ""); // Update localities based on input change
                                                }}
                                                error={methods.formState.errors.localityId}
                                                isLoadingOptions={isLoadingLocationOptions}
                                            />
                                        )}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="md:flex md:space-x-6 md:space-y-0">
                            <FormTextInput
                                label="Contact number"
                                type="tel"
                                placeholder="Enter telephone number"
                                error={formState.errors.phoneNumber}
                                tooltip={clientProfileEditTooltips.phoneNumber}
                                {...register("phoneNumber")}
                            />
                        </div>
                        <div className="md:flex md:space-x-6 md:space-y-0">
                            <FormTextInput
                                label="Company tagline"
                                placeholder="Enter company tagline"
                                error={formState.errors.companyTagline}
                                tooltip={clientProfileEditTooltips.companyTagline}
                                {...register("companyTagline")}
                            />
                        </div>
                        <div className="md:flex md:space-x-6 md:space-y-0">
                            <FormTextInput
                                label="Company website"
                                placeholder="Enter company website"
                                error={formState.errors.companyWebsite}
                                tooltip={clientProfileEditTooltips.companyWebsite}
                                {...register("companyWebsite")}
                            />
                        </div>
                    </div>
                )}
                <FormImageUploadInput
                    {...fileInputProps}
                    label="Profile image"
                    helpText="Recommended minimum size: 300x300px"
                    inputId="client-profile-image-upload-button"
                    onLoadFile={loadFile}
                    error={profileImageError}
                    tooltip={clientProfileEditTooltips.profileImage(giggedClientTerminology)}
                    dataUrl={profileImageDataUri}
                    isDirty={isProfileImageDirty}
                    previewImgClassName="object-cover"
                />
            </div>

            <GeneralFormError error={submissionError} className="ml-8" />

            <div className="md:flex mt-8">
                <Button
                    className="md:ml-auto w-full md:w-auto"
                    loading={isUpdatingClientPersonalInformation || isLoading || isValidating}
                    disabled={!formState.isDirty && !isProfileImageDirty || !!profileImageError}
                    type="submit"
                >
                    Save &amp; update
                </Button>
            </div>
        </form >
    );
};

export default ClientPersonalDetailsForm;